import React, { useState } from 'react';
import Select, { StylesConfig, GroupBase } from 'react-select';
import { useField, useFormikContext } from 'formik';
import clsx from 'clsx';
import { ErrorDisplay } from '@/components/formComponents/';

interface Option {
    label: string | number;
    value: string | number;
}

interface DropdownSelectProps {
    name: string;
    label?: string;
    placeholder?: string;
    options: Option[];
    className?: string;
    showError?: boolean;
    onChange?: (_option: Option | null) => void;
    showBorder?: boolean;
    optionSelected?: Option;
}

const DropdownSelect = ({
    showError = true,
    showBorder = false,
    className,
    name,
    label,
    placeholder,
    options,
    onChange,
    optionSelected,
}: DropdownSelectProps) => {
    const [selectedOption, setSelectedOption] = useState<Option | undefined>(
        optionSelected
    );
    const [field, meta] = useField(name);
    const { setFieldValue } = useFormikContext<Record<string, unknown>>();

    const isInErrorState = !!meta.touched && !!meta.error;

    const controlClasses = clsx(
        'p-2',
        showBorder ? 'border-2 border-[#e7e7e7]' : 'border-0 md:border-l',
        isInErrorState && 'border-2 border-red-600'
    );

    const selectStyles: StylesConfig<Option, false, GroupBase<Option>> = {
        placeholder: (defaultStyles) => ({
            ...defaultStyles,
            fontSize: '14px',
            whiteSpace: 'nowrap',
            fontStyle: 'italic',
            opacity: 0.6,
        }),
        input: (base) => ({
            ...base,
            'input:focus': {
                boxShadow: 'none',
            },
        }),
    };

    return (
        <div
            className={clsx('flex-col flex-wrap inline-flex w-full', className)}
            key={name}
        >
            {label && (
                <label className="w-full" htmlFor={name}>
                    {label}
                </label>
            )}
            <Select<Option>
                {...field}
                defaultValue={selectedOption}
                onChange={(val) => {
                    setFieldValue(name, val);
                    setSelectedOption(val ?? undefined);
                    if (onChange) onChange(val);
                }}
                options={options}
                placeholder={placeholder}
                instanceId={name}
                styles={selectStyles}
                isSearchable={false}
                unstyled={true}
                classNames={{
                    control: ({ isFocused }) =>
                        clsx(controlClasses, isFocused && 'border-red-500'),
                    menu: () =>
                        'bg-white rounded shadow-[0_0_0_1px_rgba(0,0,0,0.1)] my-1 p-2',
                    menuList: () => 'py-1',
                    option: ({ isDisabled, isFocused, isSelected }) =>
                        clsx(
                            'py-2 px-3 rounded-md transition-colors',
                            isSelected
                                ? 'bg-gray-800 text-white'
                                : isFocused
                                  ? 'bg-gray-300'
                                  : 'bg-transparent',
                            isDisabled
                                ? 'text-neutral-200'
                                : isSelected
                                  ? 'text-white'
                                  : 'text-inherit',
                            !isDisabled &&
                                (isSelected
                                    ? 'active:bg-gray-800'
                                    : 'active:bg-gray-500')
                        ),
                }}
            />
            {showError && <ErrorDisplay meta={meta} />}
        </div>
    );
};

export default DropdownSelect;
